import { useState, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import {
  Briefcase,
  Plus,
  X,
  MessageSquare,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const AddToProcess = ({ individualId, onStageUpdate }) => {
  const API = useContext(ApiContext);
  const [isOpen, setIsOpen] = useState(false);
  const [processType, setProcessType] = useState('recruitment'); // 'recruitment' ou 'bizdev'
  const [positions, setPositions] = useState([]);
  const [processBizDevs, setProcessBizDevs] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedBizDev, setSelectedBizDev] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [allCurrentProcesses, setAllCurrentProcesses] = useState([]); // Tous les processus en cours (recrutement + prospection)
  //const [comment, setComment] = useState('');
  //const [showCommentField, setShowCommentField] = useState(false);
  const [loading, setLoading] = useState(false);

  // États pour les étapes de recrutement (dynamiques selon la position sélectionnée)
  const [recruitmentSteps, setRecruitmentSteps] = useState([]);

  // Référence pour le bouton pour calculer la position du dropdown
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // État pour gérer quel projet a ses étapes déroulées
  const [expandedProject, setExpandedProject] = useState(null);

  // Charger les positions depuis l'API
  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/positions');
      const openPositions = response.data.filter((pos) => pos.open === true)
      setPositions(openPositions);
    } catch (error) {
      console.error('Error fetching positions:', error);
      toast.error('Erreur lors du chargement des positions');
    } finally {
      setLoading(false);
    }
  };

  // Charger les processus BizDev depuis l'API
  const fetchProcessBizDevs = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/process-bizdev');
      const openBizDevProcess = response.data.filter((res) => res.active === true)
      setProcessBizDevs(openBizDevProcess);
    } catch (error) {
      console.error('Error fetching process bizdev:', error);
      toast.error('Erreur lors du chargement des processus BizDev');
    } finally {
      setLoading(false);
    }
  };


  // Charger TOUS les processus en cours de l'individual (recrutement + prospection)
  const fetchAllCurrentProcesses = async () => {
    setLoading(true);
    try {
      const [positionsResponse, bizDevResponse] = await Promise.all([
        API.get('/api/positions'),
        API.get('/api/process-bizdev')
      ]);

      const allPositions = positionsResponse.data.filter((pos) => pos.open === true);
      const allBizDevProcesses = bizDevResponse.data.filter((proc) => proc.active === true);

      const currentProcesses = [];

      // Charger les processus de recrutement
      for (const position of allPositions) {
        try {
          const candidatesResponse = await API.get(`/api/positions/${position._id}/candidates`);
          const candidateProcess = candidatesResponse.data.find((c) =>
            c.individual && c.individual._id.toString() === individualId.toString()
          );

          if (candidateProcess) {
            currentProcesses.push({
              type: 'recruitment',
              id: position._id,
              title: position.title,
              partner: position.partner,
              process: candidateProcess,
              steps: position.steps.filter(step => step.active).sort((a, b) => a.order - b.order)
            });
          }
        } catch (error) {
          console.error(`Error fetching candidates for position ${position._id}:`, error);
        }
      }

      // Charger les processus de prospection
      for (const bizDevProc of allBizDevProcesses) {
        try {
          const prospectsResponse = await API.get(`/api/process-bizdev/${bizDevProc._id}/prospects`);
          const prospectProcess = prospectsResponse.data.find((p) =>
            p.individual && p.individual._id.toString() === individualId.toString()
          );

          if (prospectProcess) {
            currentProcesses.push({
              type: 'bizdev',
              id: bizDevProc._id,
              title: bizDevProc.name,
              process: prospectProcess,
              steps: bizDevProc.steps.filter(step => step.active)
            });
          }
        } catch (error) {
          console.error(`Error fetching prospects for bizdev ${bizDevProc._id}:`, error);
        }
      }

      setAllCurrentProcesses(currentProcesses);
    } catch (error) {
      console.error('Error fetching all current processes:', error);
      toast.error('Erreur lors du chargement des processus');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      // Calculer la position du dropdown
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = window.innerHeight * 0.8; // max-h-[80vh]
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Déterminer si on ouvre vers le haut ou vers le bas
        let top;
        if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
          // Ouvrir vers le bas
          top = rect.bottom + 8; // 8px de marge
        } else {
          // Ouvrir vers le haut
          top = rect.top - Math.min(dropdownHeight, spaceAbove) - 8;
        }

        setDropdownPosition({
          top: top,
          left: rect.left
        });
      }

      // Charger tous les processus en cours de l'individual
      fetchAllCurrentProcesses();
      // Charger aussi les positions et processus bizdev pour la section "Ajouter à un nouveau projet"
      fetchPositions();
      fetchProcessBizDevs();
    }
    setIsOpen(!isOpen);
    // Reset des états quand on ferme
    if (isOpen) {
      setSelectedPosition('');
      setSelectedBizDev('');
      setSelectedStage('');
/*       setComment('');
 */      //setShowCommentField(false);
    }
  };

  const handleProcessTypeChange = (newType) => {
    setProcessType(newType);
    setSelectedPosition('');
    setSelectedBizDev('');
    setSelectedStage('');
    //setComment('');
    //setShowCommentField(false);

    if (newType === 'recruitment') {
      fetchPositions();
    } else {
      fetchProcessBizDevs();
    }
  };

  // Ajouter ou déplacer un candidat dans un processus de recrutement
  const handleAddToRecruitmentProcess = async () => {
    if (!selectedPosition || !selectedStage) {
      toast.error('Veuillez sélectionner un poste et une étape');
      return;
    }

    // Trouver l'étape sélectionnée pour obtenir son nom
    const selectedStep = recruitmentSteps.find(step => step._id === selectedStage);

    // Validation du commentaire pour les étapes critiques (basée sur le nom)
    /* if (selectedStep && selectedStep.name.toLowerCase().includes('ko')) {
      toast.error('Un commentaire est requis pour un refus');
      return;
    } */

    // Vérifier si l'individual est déjà dans ce processus
    const existingProcess = allCurrentProcesses.find(
      (proc) => proc.type === 'recruitment' && proc.id === selectedPosition
    );

    setLoading(true);
    try {
      if (existingProcess) {
        // Déplacer vers une nouvelle étape (UPDATE)
        const response = await API.put(
          `/api/positions/${selectedPosition}/candidates/${individualId}`,
          {
            stageId: selectedStage,
            //comment: comment.trim()
          }
        );

        if (response.data) {
          toast.success('Candidat déplacé vers la nouvelle étape');
        }
      } else {
        // Ajouter au processus (CREATE)
        const response = await API.post(`/api/positions/${selectedPosition}/candidates`, {
          individualId: individualId,
          stageId: selectedStage,
          //comment: comment.trim()
        });

        if (response.data) {
          toast.success('Candidat ajouté au processus avec succès');
        }
      }

      fetchAllCurrentProcesses(); // Recharger tous les processus en cours
      //setComment('');
      //setShowCommentField(false);
      setSelectedStage('');
      setSelectedPosition('');

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }
    } catch (error) {
      console.error('Error adding/moving individual in process:', error);
      toast.error(existingProcess ? 'Erreur lors du déplacement' : 'Erreur lors de l\'ajout au processus');
    } finally {
      setLoading(false);
    }
  };

  // Ajouter ou déplacer un prospect dans un processus BizDev
  const handleAddToBizDevProcess = async () => {
    if (!selectedBizDev || !selectedStage) {
      toast.error('Veuillez sélectionner un processus et une étape');
      return;
    }

    // Vérifier si l'individual est déjà dans ce processus
    const existingProcess = allCurrentProcesses.find(
      (proc) => proc.type === 'bizdev' && proc.id === selectedBizDev
    );

    setLoading(true);
    try {
      if (existingProcess) {
        // Déplacer vers une nouvelle étape (UPDATE)
        const response = await API.put(
          `/api/process-bizdev/${selectedBizDev}/prospects/${individualId}/move`,
          {
            step: selectedStage
          }
        );

        if (response.data) {
          toast.success('Prospect déplacé vers la nouvelle étape');
        }
      } else {
        // Ajouter au processus (CREATE)
        const response = await API.post(`/api/process-bizdev/${selectedBizDev}/prospects`, {
          individualId: individualId,
          step: selectedStage,
          //notes: comment.trim()
        });

        if (response.data) {
          toast.success('Prospect ajouté au processus BizDev avec succès');
        }
      }

      fetchAllCurrentProcesses(); // Recharger tous les processus en cours
      //setComment('');
      //setShowCommentField(false);
      setSelectedStage('');
      setSelectedBizDev('');

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }
    } catch (error) {
      console.error('Error adding/moving individual in bizdev process:', error);
      toast.error(existingProcess ? 'Erreur lors du déplacement' : 'Erreur lors de l\'ajout au processus BizDev');
    } finally {
      setLoading(false);
    }
  };

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Vérifier si le clic est sur le bouton ou dans le dropdown
      if (
        buttonRef.current?.contains(event.target) ||
        event.target.closest('.addtoprocess-dropdown-content')
      ) {
        return;
      }

      // Fermer le dropdown principal
      setIsOpen(false);
    };

    // Petit délai pour éviter que le clic d'ouverture ne ferme immédiatement
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative process-dropdown">
      {/* Bouton principal */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`
          flex items-center space-x-2 px-3 py-1 rounded-lg border transition-all duration-200
          ${isOpen
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-gray-50 hover:border-slate-300'
          }
        `}
      >
        <span className="text-sm font-medium">Projets</span>
        <Plus size={16} />

      </button>

      {/* Dropdown avec Portal */}
      {isOpen && createPortal(
        <div
          className="addtoprocess-dropdown-content fixed bg-white border-1 border-gray-600 shadow-xl rounded-xl w-96 z-[9999] overflow-hidden max-h-[60vh] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >

          <div className="p-4 space-y-4">
            {/* Section: GESTION DES PROJETS */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                <Briefcase size={14} className="mr-1" />
                Gérer les projets
              </h4>

              <div className="space-y-3">
                {/* Sélection du type de processus */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Type de projet :
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleProcessTypeChange('recruitment')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        processType === 'recruitment'
                          ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Recrutement
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProcessTypeChange('bizdev')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        processType === 'bizdev'
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Prospection
                    </button>
                  </div>
                </div>

                {/* Liste des projets de recrutement */}
                {processType === 'recruitment' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-2">
                      Projets de recrutement :
                    </label>
                    {loading && positions.length === 0 ? (
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      </div>
                    ) : positions.length > 0 ? (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {positions.map((position) => {
                          // Vérifier si l'individual est déjà dans ce projet
                          const currentProcess = allCurrentProcesses.find(
                            (proc) => proc.type === 'recruitment' && proc.id === position._id
                          );
                          const isExpanded = expandedProject === position._id;

                          // Charger les étapes de la position si elle est déroulée
                          const positionSteps = position.steps
                            ?.filter(step => step.active)
                            ?.sort((a, b) => a.order - b.order) || [];

                          return (
                            <div key={position._id} className="bg-white">
                              {/* Header du projet */}
                              <div
                                onClick={async () => {
                                  if (isExpanded) {
                                    setExpandedProject(null);
                                  } else {
                                    setExpandedProject(position._id);
                                    // Charger les étapes si pas encore chargées
                                    if (!position.steps || position.steps.length === 0) {
                                      try {
                                        const response = await API.get(`/api/positions/${position._id}`);
                                        const positionData = response.data;
                                        // Mettre à jour la position avec les étapes
                                        setPositions(prev => prev.map(p =>
                                          p._id === position._id
                                            ? { ...p, steps: positionData.steps }
                                            : p
                                        ));
                                      } catch (error) {
                                        console.error('Error fetching position steps:', error);
                                      }
                                    }
                                  }
                                }}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <ChevronRight
                                  size={14}
                                  className={`text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                                <span className="text-xs font-medium text-slate-800 flex-1">
                                  {position.title}
                                  {position.partner?.name && (
                                    <span className="text-slate-600 ml-1">- {position.partner.name}</span>
                                  )}
                                </span>
                                {currentProcess && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full" title="Individual présent dans ce projet" />
                                )}
                              </div>

                              {/* Liste des étapes (déroulée) */}
                              {isExpanded && (
                                <div className="ml-6 space-y-1 pb-2">
                                  {positionSteps.map((step) => {
                                    const isCurrentStep = currentProcess && (
                                      step._id === currentProcess.process.stageId ||
                                      step.name === currentProcess.process.stage
                                    );

                                    return (
                                      <div
                                        key={step._id}
                                        onClick={() => {
                                          setSelectedPosition(position._id);
                                          setSelectedStage(step._id);
                                          setRecruitmentSteps(positionSteps);

                                          // Vérifier si l'étape nécessite un commentaire
                                          /* if (['ko', 'ok', 'offre'].some(keyword => step.name.toLowerCase().includes(keyword))) {
                                            setShowCommentField(true);
                                          } else {
                                            setShowCommentField(false);
                                            setComment('');
                                          } */
                                        }}
                                        className={`p-2 rounded text-xs cursor-pointer transition-colors ${
                                          isCurrentStep
                                            ? 'bg-green-100 text-green-700 font-medium'
                                            : selectedStage === step._id
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'hover:bg-gray-100 text-slate-700'
                                        }`}
                                      >
                                        {step.name}
                                        {isCurrentStep && (
                                          <span className="ml-2 text-xs">(actuel)</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-dashed border-slate-300 rounded-lg p-4 text-center">
                        <p className="text-xs text-slate-500">Aucun projet de recrutement</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Liste des projets de prospection */}
                {processType === 'bizdev' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-2">
                      Projets de prospection :
                    </label>
                    {loading && processBizDevs.length === 0 ? (
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      </div>
                    ) : processBizDevs.length > 0 ? (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {processBizDevs.map((process) => {
                          // Vérifier si l'individual est déjà dans ce projet
                          const currentProcess = allCurrentProcesses.find(
                            (proc) => proc.type === 'bizdev' && proc.id === process._id
                          );
                          const isExpanded = expandedProject === process._id;

                          // Récupérer les étapes actives du processus
                          const processSteps = process.steps?.filter(step => step.active) || [];

                          return (
                            <div key={process._id} className="bg-white">
                              {/* Header du projet */}
                              <div
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandedProject(null);
                                  } else {
                                    setExpandedProject(process._id);
                                  }
                                }}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <ChevronRight
                                  size={14}
                                  className={`text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                                <span className="text-xs font-medium text-slate-800 flex-1">
                                  {process.name}
                                </span>
                                {currentProcess && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full" title="Individual présent dans ce projet" />
                                )}
                              </div>

                              {/* Liste des étapes (déroulée) */}
                              {isExpanded && (
                                <div className="ml-6 space-y-1 pb-2">
                                  {processSteps.map((step) => {
                                    const isCurrentStep = currentProcess && step.name === currentProcess.process.currentStep;

                                    return (
                                      <div
                                        key={step._id}
                                        onClick={() => {
                                          setSelectedBizDev(process._id);
                                          setSelectedStage(step.name);

                                          // Vérifier si l'étape nécessite un commentaire
                                          /* if (['ko', 'ok', 'offre'].some(keyword => step.name.toLowerCase().includes(keyword))) {
                                            setShowCommentField(true);
                                          } else {
                                            setShowCommentField(false);
                                            setComment('');
                                          } */
                                        }}
                                        className={`p-2 rounded text-xs cursor-pointer transition-colors ${
                                          isCurrentStep
                                            ? 'bg-green-100 text-green-700 font-medium'
                                            : selectedStage === step.name
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'hover:bg-gray-100 text-slate-700'
                                        }`}
                                      >
                                        {step.name}
                                        {isCurrentStep && (
                                          <span className="ml-2 text-xs">(actuel)</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-dashed border-slate-300 rounded-lg p-4 text-center">
                        <p className="text-xs text-slate-500">Aucun projet de prospection</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Champ de commentaire conditionnel */}
                  {/* {showCommentField && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2 flex items-center">
                        <MessageSquare size={12} className="mr-1" />
                        Commentaire
                        {selectedStage === 'ko' && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        rows="3"
                        placeholder={
                          selectedStage === 'ko'
                            ? "Expliquer les raisons du refus..."
                            : "Ajouter un commentaire..."
                        }
                        disabled={loading}
                      />
                      {selectedStage === 'ko' && (
                        <p className="text-xs text-red-600 mt-1">
                          Un commentaire explicatif est obligatoire pour un refus
                        </p>
                      )}
                    </div>
                  )} */}

                  {/* Boutons d'action */}
                  {((processType === 'recruitment' && selectedPosition && selectedStage) ||
                    (processType === 'bizdev' && selectedBizDev && selectedStage)) && (
                    <div className="flex space-x-2 pt-3 border-t">
                      <button
                        onClick={() => {
                          if (processType === 'recruitment') {
                            handleAddToRecruitmentProcess();
                          } else {
                            handleAddToBizDevProcess();
                          }
                        }}
                        disabled={loading}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-2 px-3 rounded-lg text-xs font-medium transition-colors flex items-center justify-center space-x-1"
                      >
                        {loading ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Plus size={14} />
                            <span>
                              {(() => {
                                // Vérifier si l'individual est déjà dans le processus sélectionné
                                if (processType === 'recruitment' && selectedPosition) {
                                  const existingProcess = allCurrentProcesses.find(
                                    (proc) => proc.type === 'recruitment' && proc.id === selectedPosition
                                  );
                                  return existingProcess ? 'Déplacer' : 'Ajouter';
                                } else if (processType === 'bizdev' && selectedBizDev) {
                                  const existingProcess = allCurrentProcesses.find(
                                    (proc) => proc.type === 'bizdev' && proc.id === selectedBizDev
                                  );
                                  return existingProcess ? 'Déplacer' : 'Ajouter';
                                }
                                return 'Ajouter';
                              })()}
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AddToProcess;